const CHOICE_KEY = "axis.marketing.analytics.consent.v1"
const SIX_MONTHS = 180 * 24 * 60 * 60 * 1000
const FORMS = new Set(["university_inquiry"])

export function safeUrl(value, base) {
  if (!value) return ""
  try {
    const url = new URL(value, base)
    if (!["https:", "http:"].includes(url.protocol)) return ""
    // The site's public routes contain no personal identifiers. Drop queries and fragments.
    return url.origin + url.pathname
  } catch { return "" }
}

export function marketingPath(value, base) {
  try {
    const url = new URL(value, base)
    return url.origin === new URL(base).origin && ["/", "/university"].includes(url.pathname)
  } catch { return false }
}

export function installAnalytics(win, doc) {
  if (!marketingPath(win.location.href, win.location.href)) return
  const id = doc.querySelector('meta[name="google-analytics-id"]')?.content
  if (!/^G-[A-Z0-9]+$/.test(id || "")) return
  const read = (storage, key) => { try { return JSON.parse(storage.getItem(key)) } catch { return null } }
  const write = (storage, key, value) => { try { storage.setItem(key, JSON.stringify(value)) } catch { /* Storage may be unavailable. */ } }
  let choice = null
  try {
    const saved = read(win.localStorage, CHOICE_KEY)
    if (saved?.expires > Date.now() && ["granted", "denied"].includes(saved.value)) choice = saved.value
  } catch { /* No persistent storage. */ }
  let started = false
  let pageSent = false
  let scrolled = false
  // Never report an admin/profile URL as the referrer when returning to marketing.
  let previousPage = ""
  try {
    if (doc.referrer) {
      const referrer = new URL(doc.referrer)
      previousPage = referrer.origin === new URL(win.location.href).origin
        ? (marketingPath(doc.referrer, win.location.href) ? safeUrl(doc.referrer) : "")
        : referrer.origin + "/"
    }
  } catch { /* Ignore malformed referrers. */ }
  let lastPage = null
  let lastVisitKey = null
  let startedForms = new WeakSet()
  const seenLeads = new Set()
  win.dataLayer = win.dataLayer || []
  win.gtag = win.gtag || function () { win.dataLayer.push(arguments) }
  win[`ga-disable-${id}`] = choice !== "granted"

  function context() {
    return {
      page_location: safeUrl(win.location.href),
      page_referrer: previousPage,
      page_title: doc.title,
      language: doc.documentElement.lang === "fr" ? "fr" : "en"
    }
  }

  function start() {
    if (started || choice !== "granted") return
    started = true
    win[`ga-disable-${id}`] = false
    win.gtag("consent", "default", { analytics_storage: "granted", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" })
    win.gtag("js", new Date())
    win.gtag("config", id, {
      ...context(), send_page_view: false, allow_google_signals: false,
      allow_ad_personalization_signals: false, cookie_expires: 15552000, cookie_domain: win.location.hostname
    })
    const script = doc.createElement("script")
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
    doc.head.appendChild(script)
  }

  function event(name, parameters = {}) {
    if (choice !== "granted" || !marketingPath(win.location.href, win.location.href)) return
    start()
    win.gtag("event", name, { ...context(), ...parameters, send_to: id })
  }

  function pageView() {
    if (pageSent || choice !== "granted") return
    pageSent = true
    start()
    win.gtag("set", context())
    event("page_view")
  }

  function successes() {
    doc.querySelectorAll("[data-analytics-success]").forEach(marker => {
      const token = marker.dataset.analyticsSuccess
      const form = marker.dataset.analyticsForm
      marker.remove()
      if (!token || !FORMS.has(form)) return
      let stored = []
      try { stored = read(win.sessionStorage, "axis.marketing.analytics.leads") || [] } catch { /* No storage. */ }
      if (!Array.isArray(stored)) stored = []
      if (seenLeads.has(token) || stored.includes(token)) return
      seenLeads.add(token)
      try { write(win.sessionStorage, "axis.marketing.analytics.leads", [...stored, token].slice(-100)) } catch { /* No storage. */ }
      // A submission without consent is discarded, never replayed after later acceptance.
      event("generate_lead", { form_id: form })
    })
  }

  function showChoice() {
    const banner = doc.querySelector("[data-analytics-consent]")
    if (banner) banner.hidden = choice !== null
  }

  function clearCookies() {
    const parts = win.location.hostname.split(".")
    const domains = ["", ...parts.map((_, i) => `; domain=.${parts.slice(i).join(".")}`)]
    doc.cookie.split(";").forEach(cookie => {
      const name = cookie.split("=")[0].trim()
      if (name === "_ga" || name.startsWith("_ga_")) {
        domains.forEach(domain => { doc.cookie = `${name}=; Max-Age=0; path=/${domain}` })
      }
    })
  }

  function choose(value) {
    choice = value
    try { write(win.localStorage, CHOICE_KEY, { value, expires: Date.now() + SIX_MONTHS }) } catch { /* No storage. */ }
    showChoice()
    if (value === "granted") {
      win[`ga-disable-${id}`] = false
      if (started) win.gtag("consent", "update", { analytics_storage: "granted" })
      pageView()
    } else {
      win[`ga-disable-${id}`] = true
      clearCookies()
      // Reload removes the previously loaded tag and its automatic engagement listeners.
      if (started) win.location.reload()
    }
  }

  doc.addEventListener("click", e => {
    const button = e.target.closest("[data-analytics-choice]")
    if (button) { choose(button.dataset.analyticsChoice); return }
    if (e.target.closest("[data-analytics-settings]")) {
      const banner = doc.querySelector("[data-analytics-consent]")
      if (banner) { banner.hidden = false; banner.querySelector("button")?.focus() }
      return
    }
    const link = e.target.closest("a[data-analytics-click]")
    if (link) event("cta_click", { link_id: link.dataset.analyticsClick, link_url: safeUrl(link.href, win.location.href) })
  })
  doc.addEventListener("focusin", e => {
    const form = e.target.closest("form[data-analytics-form]")
    if (!form || startedForms.has(form) || choice !== "granted") return
    startedForms.add(form)
    event("form_start", { form_id: form.dataset.analyticsForm })
  })
  win.addEventListener("scroll", () => {
    const height = doc.documentElement.scrollHeight
    if (!scrolled && choice === "granted" && height > win.innerHeight && win.scrollY + win.innerHeight >= height * .9) {
      scrolled = true
      event("scroll", { percent_scrolled: 90 })
    }
  }, { passive: true })
  // Restore consent and a fresh page visit when returning from the browser cache.
  win.addEventListener("pageshow", e => {
    if (e.persisted) win.location.reload()
  })
  win.addEventListener("storage", e => {
    if (e.key === CHOICE_KEY) win.location.reload()
  })
  doc.addEventListener("turbo:before-cache", () => {
    doc.querySelectorAll("[data-analytics-success]").forEach(marker => marker.remove())
  })
  // A full navigation discards the tag before any non-marketing document is shown.
  doc.addEventListener("turbo:before-visit", e => {
    if (!marketingPath(e.detail.url, win.location.href)) {
      e.preventDefault()
      win[`ga-disable-${id}`] = true
      win.location.assign(e.detail.url)
    }
  })
  function pageLoaded() {
    const url = new URL(win.location.href)
    const visitKey = url.origin + url.pathname + url.search
    if (visitKey === lastVisitKey) { showChoice(); successes(); return }
    lastVisitKey = visitKey
    if (lastPage) previousPage = lastPage
    lastPage = safeUrl(win.location.href)
    pageSent = false
    scrolled = false
    startedForms = new WeakSet()
    showChoice()
    pageView()
    successes()
  }
  doc.addEventListener("turbo:load", pageLoaded)
  if (doc.readyState === "loading") doc.addEventListener("DOMContentLoaded", pageLoaded, { once: true })
  else pageLoaded()
  new win.MutationObserver(successes).observe(doc.body, { childList: true, subtree: true })
  showChoice()
}
